import { apiRequest, HttpMethod } from '@hqo/shared-modules/dist/api-request';
import { useApi } from 'app/shared/contexts/api';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { Building, GetBuildingAPIResponse, GetBuildingQueryResponse } from 'app/data/types/building';
import { useOpenParams } from 'app/shared/hooks/use-open-params/use-open-params.hook';
import { useEffect } from 'react';
import { useErrors } from 'app/shared/contexts/errors';
import { ERROR_DEFAULT } from 'app/shared/consts/error-codes';

export const BUILDING_QUERY_KEY = 'building';

export const getUseBuildingQueryKey = (buildingUuid: string): QueryKey => [BUILDING_QUERY_KEY, buildingUuid];

export const useBuildingQuery = (sdkBuilding?: Building | null): GetBuildingQueryResponse => {
  const { addGenericError, hideGenericErrors } = useErrors();
  const { apiUrl, getAuthHeaders, getDefaultHeaders } = useApi();
  const { buildingUuid } = useOpenParams();
  const queryKey = getUseBuildingQueryKey(buildingUuid);

  const getBuildingRequest = async (): Promise<Building | null> => {
    const headers = await getAuthHeaders();
    const requestHeaders = { ...headers, ...getDefaultHeaders() };

    const response = await apiRequest<GetBuildingAPIResponse, void, void>({
      url: `${apiUrl}/api/tenant/v1/buildings/${buildingUuid}`,
      method: HttpMethod.GET,
      headers: requestHeaders,
    });

    if (response && !response.isEmpty()) {
      return response.data.data.building;
    }

    return null;
  };

  const { data, error, isLoading, isSuccess } = useQuery<Building | null, Error>({
    queryKey,
    queryFn: getBuildingRequest,
    enabled: !!buildingUuid && !!apiUrl && !!getAuthHeaders && !!getDefaultHeaders && !sdkBuilding,
    retry: false,
  });

  useEffect(() => {
    if (error) {
      addGenericError(BUILDING_QUERY_KEY, { error, errorCode: ERROR_DEFAULT });
    } else {
      hideGenericErrors(BUILDING_QUERY_KEY);
    }
  }, [error, addGenericError, hideGenericErrors]);

  return {
    building: sdkBuilding ?? data ?? null,
    error: error ? { error, errorCode: ERROR_DEFAULT } : null,
    isLoading,
    isSuccess,
  };
};
